import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  FactCheck as CountIcon,
  PlaceOutlined as LocationIcon,
  EditNote as DraftIcon,
  Straighten as VarianceIcon,
  TaskAlt as PostIcon,
  TipsAndUpdates as TipIcon,
} from '@mui/icons-material';

/**
 * STOCKCOUNT-HELP.1 — "how stock counts work" guide, opened from the Stock
 * Counts list header. Follows the LotsHelpModal / InventoryModuleHelpModal
 * visual pattern. Content-only: no behaviour changes to counts.
 */

// ── Sub-components (LotsHelpModal pattern) ─────────────────────────────────────

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => {
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ color: theme.palette.primary.main, display: 'flex', fontSize: 18 }}>
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

interface FieldRowProps {
  label: string;
  children: React.ReactNode;
  accent?: boolean;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, children, accent }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        py: 0.75,
        px: 1.25,
        borderRadius: 1.5,
        '&:hover': { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          minWidth: 150,
          flexShrink: 0,
          color: accent ? theme.palette.primary.main : theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
};

interface NoteProps {
  children: React.ReactNode;
  variant?: 'info' | 'warning';
}

const Note: React.FC<NoteProps> = ({ children, variant = 'info' }) => {
  const theme = useTheme();
  const isWarning = variant === 'warning';
  return (
    <Box
      sx={{
        mt: 1.25,
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: isWarning
          ? (theme.palette.mode === 'dark' ? 'rgba(250,173,20,0.1)' : '#fffbeb')
          : (theme.palette.mode === 'dark' ? 'rgba(9,105,218,0.1)' : '#f0f7ff'),
        borderLeft: `3px solid ${isWarning ? '#d97706' : theme.palette.primary.main}`,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

const StockCountHelpModal: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(30,30,50,0.35)',
          },
        },
      }}
      PaperProps={{
        sx: {
          background: isDark ? 'rgba(22, 22, 35, 0.90)' : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          boxShadow: isDark ? '0 32px 80px rgba(0,0,0,0.6)' : '0 32px 80px rgba(0,0,0,0.16)',
          borderRadius: 3,
          maxHeight: '88vh',
        },
      }}
    >
      {/* ── Sticky header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CountIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Stock Counts Guide
            </Typography>
            <Typography variant="caption" color="text.secondary">
              How counting works — one count per location
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.action.hover },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Scrollable body ─────────────────────────────────────────────────── */}
      <DialogContent sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3.5 }}>

        {/* Intro — compressed from docs/stock-count-manual.md (Overview) */}
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          A stock count lets you physically count your inventory and reconcile it against what the
          system believes you have. When you post, the system compares counted against expected and
          adjusts the balance for every discrepancy — each adjustment is a permanent{' '}
          <strong>COUNT_VARIANCE</strong> movement in the ledger.
        </Typography>

        <Divider />

        {/* ── Location scope — the key rule ───────────────────────────────────── */}
        <Section icon={<LocationIcon fontSize="inherit" />} title="One Count = One Location">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Every count has a fixed scope chosen at creation: one specific location (or the
            venue-name option for items not assigned to any location). The scope cannot be changed
            afterwards, and items from a different scope cannot be added — expected quantities,
            counted quantities, and posting adjustments all apply to that location only.
          </Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Multiple locations" accent>
              If your venue has Main Stock, Bar, Cellar, Kitchen or Storage, create a separate
              count for each one. Five locations means five counts.
            </FieldRow>
            <FieldRow label="Expected quantity">
              The expected quantity shown per item is for the selected location only — not the
              venue-wide total.
            </FieldRow>
            <FieldRow label="What to count">
              Count only the bottles physically at that location. A bottle sitting at the Bar does
              not belong on the Cellar's sheet, even if it "should" be in the Cellar.
            </FieldRow>
          </Box>
          <Note variant="warning">
            <strong>Do not count another location's stock on this sheet.</strong> Counting Bar
            bottles on a Cellar count posts a false gain in the Cellar while the Bar still shows
            stock that was never counted. If bottles are in the wrong place, move them with a
            Transfer first — then count.
          </Note>
        </Section>

        <Divider />

        {/* ── Lifecycle ───────────────────────────────────────────────────────── */}
        <Section icon={<DraftIcon fontSize="inherit" />} title="Draft vs Posted">
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Draft" accent>
              A working sheet. Add or remove items (search within scope, or load all items in
              scope at once), enter quantities, edit the date and notes, or delete the count.
              Nothing changes in stock while a count is a draft. Each location can have{' '}
              <strong>one open draft at a time</strong> — finish or delete it before starting
              another.
            </FieldRow>
            <FieldRow label="Posted" accent>
              View only — locked forever. Posting is one-way: it cannot be undone or edited. A
              wrong posting needs a corrective manual adjustment or a second count. At posting,
              expected quantities are <strong>refreshed from live stock</strong>, so variance
              always compares your count against reality at that moment — not against numbers
              that may have gone stale while you were counting.
            </FieldRow>
          </Box>
        </Section>

        <Divider />

        {/* ── Reading the sheet ───────────────────────────────────────────────── */}
        <Section icon={<VarianceIcon fontSize="inherit" />} title="Expected, Counted, Variance">
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Expected">
              Frozen at the moment each item is added to the count — it reflects the balance at
              that instant and does not update if stock moves while the count is open.
            </FieldRow>
            <FieldRow label="Counted">
              The number of bottles you physically found. Saved automatically when you leave the
              field; you can change it any time while the count is a draft.
            </FieldRow>
            <FieldRow label="Variance">
              Counted − Expected. Positive (+) means you found more than expected; negative (−)
              means fewer — shrinkage, breakage or a data error. Zero variance creates no movement.
            </FieldRow>
          </Box>
          <Note>
            Entering <strong>0</strong> means "I counted and found none" and will post the full
            loss. Leaving the field <strong>blank</strong> means "not counted" — blank lines are
            skipped at posting and create no change.
          </Note>
        </Section>

        <Divider />

        {/* ── Posting ─────────────────────────────────────────────────────────── */}
        <Section icon={<PostIcon fontSize="inherit" />} title="What Posting Does">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Review &amp; Post shows every line with a non-zero variance — these are the items that
            will create adjustments. To post, the count date must be set and at least one line must
            be counted. Posting then locks the count and, for each variance line, writes one
            COUNT_VARIANCE movement and updates that location's balance immediately. Other
            locations are untouched.
          </Typography>
          <Note>
            A posted count becomes a permanent read-only record, and provides the{' '}
            <strong>Inventory Cycle Report</strong> download — a full reconciliation of expected vs
            counted with movement detail.
          </Note>
        </Section>

        <Divider />

        {/* ── Best practice ───────────────────────────────────────────────────── */}
        <Section icon={<TipIcon fontSize="inherit" />} title="Best Practice">
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="One location at a time" accent>
              Finish and post one location before starting the next. It keeps sheets short and
              variances easy to explain.
            </FieldRow>
            <FieldRow label="Move first, count second">
              Do transfers for any misplaced stock before counting, so each sheet reflects where
              bottles actually live.
            </FieldRow>
            <FieldRow label="Load all items in scope">
              For a full periodic count, use the bulk load so every item at the location lands on
              the sheet — you will not miss anything, and items already on the count are skipped.
            </FieldRow>
            <FieldRow label="Count promptly">
              You do not have to pause operations, but expected quantities are frozen when lines
              are added — stock that moves after that will show up as variance. Counting during
              quiet periods keeps variances meaningful.
            </FieldRow>
          </Box>
        </Section>

        {/* ── Bottom spacer ──────────────────────────────────────────────────── */}
        <Box sx={{ height: 8 }} />

      </DialogContent>

      {/* ── Sticky footer ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'flex-end',
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '0 0 12px 12px',
        }}
      >
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ flex: 1, alignSelf: 'center', fontSize: '0.72rem' }}
        >
          Remember: one stock count covers one location.
        </Typography>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.15s',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.text.secondary,
            },
          }}
        >
          Close
        </Box>
      </Box>
    </Dialog>
  );
};

export default StockCountHelpModal;
